// src/pages/Convocations/components/ConvocationCard.jsx

import React from "react";

import {
  FaCalendarDay,
  FaCheck,
  FaCommentDots,
  FaMapMarkerAlt,
  FaUserTie,
} from "react-icons/fa";

import ConvocationStatusBadge from "../ui/ConvocationStatusBadge";
import ConvocationActions from "./ConvocationActions";
import ConvocationSupportBadges from "./ConvocationSupportBadges";

import {
  formatDateLong,
  formatDateTime,
  getConvocationActorLabel,
  getEventLocation,
} from "../utils/convocation.utils";

export default function ConvocationCard({
  convocation,
  saving,
  onOpenEvent,
  onOpenText,
  onRespond,
}) {
  const event = convocation?.Event || {};

  const hasObservations = Boolean(
    convocation?.observations,
  );

  const hasRejectReason = Boolean(
    convocation?.response_reason,
  );

  const hasSupportComment = Boolean(
    convocation?.support_request_comment,
  );

  return (
    <article
      className="
        overflow-hidden
        rounded-3xl
        border border-slate-200/80
        bg-white
        shadow-[0_18px_55px_-35px_rgba(15,23,42,0.55)]
        transition-shadow duration-300
        hover:shadow-[0_24px_65px_-38px_rgba(15,23,42,0.6)]
        dark:border-slate-800
        dark:bg-slate-900
      "
    >
      {/* En-tête */}
      <div
        className="
          border-b border-slate-100
          bg-gradient-to-r
          from-slate-50
          to-blue-50/60
          px-4 py-4
          dark:border-slate-800
          dark:from-slate-900
          dark:to-blue-950/30
          sm:px-5
        "
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-3">
            <div
              className="
                flex h-12 w-12
                shrink-0 items-center
                justify-center
                rounded-2xl
                bg-blue-700
                text-white
                shadow-lg
                shadow-blue-950/20
              "
            >
              <FaCalendarDay className="text-lg" />
            </div>

            <div className="min-w-0">
              <p
                className="
                  text-xs font-semibold
                  capitalize
                  text-blue-700
                  dark:text-blue-300
                "
              >
                {formatDateLong(event.start)}
              </p>

              <h2
                className="
                  mt-1
                  font-satoshi
                  text-lg font-bold
                  leading-snug
                  text-slate-950
                  dark:text-white
                  sm:text-xl
                "
              >
                {event.titre || "Événement"}
              </h2>
            </div>
          </div>

          <ConvocationStatusBadge
            status={convocation.response_status}
            compact
          />
        </div>

        <div
          className="
            mt-3 flex flex-wrap
            gap-2
            text-xs font-medium
            text-slate-600
            dark:text-slate-300
          "
        >
          <span
            className="
              inline-flex items-center
              gap-1.5 rounded-full
              bg-white
              px-2.5 py-1.5
              shadow-sm
              dark:bg-slate-800
            "
          >
            <FaMapMarkerAlt className="shrink-0 text-rose-500" />

            <span className="line-clamp-1">
              {getEventLocation(event)}
            </span>
          </span>

          {event.type_event ? (
            <span
              className="
                rounded-full
                bg-white
                px-2.5 py-1.5
                shadow-sm
                dark:bg-slate-800
              "
            >
              {event.type_event}
            </span>
          ) : null}
        </div>
      </div>

      <div className="space-y-4 p-4 sm:p-5">
        {/* Affectation et profil */}
        <div
          className="
            grid grid-cols-1
            gap-3
            sm:grid-cols-2
          "
        >
          <div
            className="
              rounded-2xl
              border border-slate-100
              bg-slate-50
              p-3
              dark:border-slate-800
              dark:bg-slate-950
            "
          >
            <p
              className="
                text-[10px] font-semibold
                uppercase
                tracking-[0.12em]
                text-slate-400
              "
            >
              Affectation
            </p>

            <p
              className="
                mt-1.5 inline-flex
                items-center gap-2
                text-sm font-semibold
                text-slate-800
                dark:text-white
              "
            >
              <FaUserTie className="text-blue-600 dark:text-blue-400" />

              {convocation.poste || "Non précisée"}
            </p>

            {convocation.tapis ? (
              <p
                className="
                  mt-1 text-xs
                  font-medium
                  text-slate-500
                  dark:text-slate-400
                "
              >
                Tapis :{" "}
                <span className="font-semibold">
                  {convocation.tapis}
                </span>
              </p>
            ) : null}
          </div>

          <div
            className="
              rounded-2xl
              border border-slate-100
              bg-slate-50
              p-3
              dark:border-slate-800
              dark:bg-slate-950
            "
          >
            <p
              className="
                text-[10px] font-semibold
                uppercase
                tracking-[0.12em]
                text-slate-400
              "
            >
              Profil concerné
            </p>

            <p
              className="
                mt-1.5 text-sm
                font-semibold
                text-slate-800
                dark:text-white
              "
            >
              {getConvocationActorLabel(convocation)}
            </p>

            {convocation?.acteur?.club_acteur ? (
              <p
                className="
                  mt-1 line-clamp-1
                  text-xs font-medium
                  text-slate-500
                  dark:text-slate-400
                "
              >
                {convocation.acteur.club_acteur}
              </p>
            ) : null}
          </div>
        </div>

        <ConvocationSupportBadges
          convocation={convocation}
        />

        {/* Informations complémentaires */}
        <div className="flex flex-wrap gap-2">
          {hasObservations ? (
            <button
              type="button"
              onClick={() =>
                onOpenText({
                  title: "Observations des responsables",
                  content: convocation.observations,
                  allowHtml: true,
                })
              }
              className="
                inline-flex min-h-9
                items-center gap-2
                rounded-xl
                bg-blue-50
                px-3
                text-xs font-semibold
                text-blue-700
                transition
                hover:bg-blue-100
                dark:bg-blue-950/40
                dark:text-blue-300
                dark:hover:bg-blue-950/70
              "
            >
              <FaCommentDots />
              Observations
            </button>
          ) : null}

          {hasRejectReason ? (
            <button
              type="button"
              onClick={() =>
                onOpenText({
                  title: "Motif de votre refus",
                  content: convocation.response_reason,
                  allowHtml: false,
                })
              }
              className="
                inline-flex min-h-9
                items-center gap-2
                rounded-xl
                bg-rose-50
                px-3
                text-xs font-semibold
                text-rose-700
                transition
                hover:bg-rose-100
                dark:bg-rose-950/40
                dark:text-rose-300
                dark:hover:bg-rose-950/70
              "
            >
              <FaCommentDots />
              Motif du refus
            </button>
          ) : null}

          {hasSupportComment ? (
            <button
              type="button"
              onClick={() =>
                onOpenText({
                  title: "Commentaire de prise en charge",
                  content:
                    convocation.support_request_comment,
                  allowHtml: false,
                })
              }
              className="
                inline-flex min-h-9
                items-center gap-2
                rounded-xl
                bg-violet-50
                px-3
                text-xs font-semibold
                text-violet-700
                transition
                hover:bg-violet-100
                dark:bg-violet-950/40
                dark:text-violet-300
                dark:hover:bg-violet-950/70
              "
            >
              <FaCommentDots />
              Prise en charge
            </button>
          ) : null}
        </div>

        {/* Informations administratives */}
        <div
          className="
            flex flex-col gap-2
            rounded-2xl
            border border-slate-100
            bg-slate-50
            p-3
            text-xs font-medium
            text-slate-500
            dark:border-slate-800
            dark:bg-slate-950
            dark:text-slate-400
          "
        >
          <div className="flex items-center justify-between gap-3">
            <span>Validation administrative</span>

            <span
              className={
                convocation.is_validate
                  ? [
                      "inline-flex",
                      "items-center",
                      "gap-1",
                      "font-semibold",
                      "text-emerald-600",
                      "dark:text-emerald-400",
                    ].join(" ")
                  : "font-semibold text-slate-500"
              }
            >
              {convocation.is_validate ? <FaCheck /> : null}

              {convocation.is_validate
                ? "Validée"
                : "Non validée"}
            </span>
          </div>

          {convocation.note != null ? (
            <div className="flex items-center justify-between gap-3">
              <span>Note</span>

              <span
                className="
                  font-semibold
                  text-slate-700
                  dark:text-slate-200
                "
              >
                {convocation.note}
              </span>
            </div>
          ) : null}

          {convocation.responded_at ? (
            <div className="flex items-center justify-between gap-3">
              <span>Dernière réponse</span>

              <span
                className="
                  text-right font-semibold
                  text-slate-700
                  dark:text-slate-200
                "
              >
                {formatDateTime(convocation.responded_at)}
              </span>
            </div>
          ) : null}
        </div>

        <ConvocationActions
          convocation={convocation}
          saving={saving}
          stacked
          onOpenEvent={onOpenEvent}
          onRespond={onRespond}
        />
      </div>
    </article>
  );
}